import React from "react";
import { Alert, Box, Button, Stack } from "@mui/material";
import { SubmitHandler, useForm } from "react-hook-form";
import { Modal } from "../../components/Modal";
import InputField from "../../components/InputField";
import { useApiRequest } from "../../hooks/useApiRequest";
import { ConstructionGroup } from "../../types/constructionGroup";

type FormData = {
  groupName: string;
};

type NewEditConstructionGroupModalProps = {
  isOpen: boolean;
  onSaveSuccessful: () => void;
  onClose: () => void;
  group?: ConstructionGroup;
  isNew: boolean;
};

const getDefaultValues = (group?: ConstructionGroup): FormData => {
  return {
    groupName: group?.groupName ?? "",
  };
};

export const NewEditConstructionGroupModal: React.FC<NewEditConstructionGroupModalProps> = ({
  isOpen,
  onSaveSuccessful,
  onClose,
  group,
  isNew,
}) => {
  const {
    loading: saveGroupLoading,
    request: saveGroupRequest,
    error: saveGroupError,
  } = useApiRequest<ConstructionGroup>(true);

  const { register, handleSubmit, formState } = useForm<FormData>({
    defaultValues: getDefaultValues(group),
    reValidateMode: "onChange",
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<FormData> = (data: FormData) => {
    if (isNew) {
      saveGroupRequest(
        "/retailer/constoptions/groups/add",
        {
          method: "POST",
          data,
        },
        {
          onSuccess: onSaveSuccessful,
        }
      );
    } else {
      saveGroupRequest(
        `/retailer/constoptions/groups/${group?.constructionGroupGuid}/edit`,
        {
          method: "POST",
          data,
        },
        {
          onSuccess: onSaveSuccessful,
        }
      );
    }
  };

  return (
    <Modal
      heading={isNew ? "New Group" : "Edit Group"}
      isOpen={isOpen}
      onClose={onClose}
    >
      <Box sx={{ width: { xs: "100%", sm: "371px" } }}>
        {saveGroupError && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {saveGroupError.response?.status === 409
              ? "A group already exists with this name."
              : "Something went wrong. Please try again."}
          </Alert>
        )}
        <Stack component="form" onSubmit={handleSubmit(onSubmit)} spacing={3}>
          <InputField
            label="Group Name"
            placeholder="Enter name"
            fullWidth
            required
            {...register("groupName", { required: "Enter name" })}
            error={!!formState.errors.groupName}
            helperText={formState.errors.groupName?.message}
          />
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{ marginRight: 2 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              color="primary"
              disabled={saveGroupLoading}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
};
